import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Collapse,
  Badge,
  makeStyles
} from "@material-ui/core";

// Ícones
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import QuestionAnswerOutlinedIcon from "@material-ui/icons/QuestionAnswerOutlined";
import AssignmentIcon from "@material-ui/icons/Assignment";
import StorageIcon from "@material-ui/icons/Storage";
import BarChartIcon from "@material-ui/icons/BarChart";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import ConfirmationNumberIcon from "@material-ui/icons/ConfirmationNumber";
import ComputerIcon from "@material-ui/icons/Computer";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";

// Estilos visuais adicionais
const useStyles = makeStyles((theme) => ({
  nested: {
    paddingLeft: theme.spacing(4),
  },
  menuItem: {
    borderRadius: 8,
    margin: "2px 8px",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  activeSubmenu: {
    backgroundColor: theme.palette.action.selected,
  }
}));

function ListItemLink(props) {
  const { icon, primary, to, className } = props;

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  return (
    <li>
      <ListItem button component={renderLink} className={className}>
        {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
        <ListItemText primary={primary} />
      </ListItem>
    </li>
  );
}

const MainListItems = (props) => {
  const classes = useStyles();
  const { drawerClose } = props;
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const [connectionWarning, setConnectionWarning] = useState(false);

  // Estados dos Menus Dropdown (Sanfona)
  const [openTicketsMenu, setOpenTicketsMenu] = useState(false);
  const [openAdminMenu, setOpenAdminMenu] = useState(false);
  const [openReportsMenu, setOpenReportsMenu] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  return (
    <List component="nav" onClick={drawerClose}>
      {/* Principal */}
      <ListItemLink
        to="/"
        primary="Dashboard"
        icon={<DashboardOutlinedIcon />}
        className={classes.menuItem}
      />

      <ListItemLink
        to="/connections"
        primary={i18n.t("mainDrawer.listItems.connections")}
        icon={
          <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
            <SyncAltIcon />
          </Badge>
        }
        className={classes.menuItem}
      />

      <ListItemLink
        to="/contacts"
        primary={i18n.t("mainDrawer.listItems.contacts")}
        icon={<ContactPhoneOutlinedIcon />}
        className={classes.menuItem}
      />

      <ListItemLink
        to="/quickAnswers"
        primary={i18n.t("mainDrawer.listItems.quickAnswers")}
        icon={<QuestionAnswerOutlinedIcon />}
        className={classes.menuItem}
      />

      <ListItemLink
        to="/tickets"
        primary={i18n.t("mainDrawer.listItems.tickets")}
        icon={<WhatsAppIcon />}
        className={`${classes.menuItem} ${classes.nested}`}
      />

      {/* Acessos Remotos: usando ListItem nativo para abrir em target="_blank" sem erros */}
      <ListItem
        button
        onClick={() => window.open("/acessos-remotos", "_blank")}
        className={`${classes.menuItem} ${classes.nested}`}
      >
        <ListItemIcon>
          <ComputerIcon />
        </ListItemIcon>
        <ListItemText primary="Acessos Remotos" />
      </ListItem>

      <Divider style={{ margin: "8px 0" }} />

      {/* DROPDOWN 1: ATENDIMENTOS E TICKETS */}
      <ListItem
        button
        onClick={() => setOpenTicketsMenu(!openTicketsMenu)}
        className={classes.menuItem}
      >
        <ListItemIcon>
          <ConfirmationNumberIcon />
        </ListItemIcon>
        <ListItemText primary="Tickets" />
        {openTicketsMenu ? <ExpandLess /> : <ExpandMore />}
      </ListItem>

      <Collapse in={openTicketsMenu} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemLink
            to="/Custom-tickets-bonus"
            primary="Tickets Bonificados"
            icon={<AssignmentIcon />}
            className={`${classes.menuItem} ${classes.nested}`}
          />
          <ListItemLink
            to="/custom-tickets"
            primary="Tickets Avulso"
            icon={<AssignmentIcon />}
            className={`${classes.menuItem} ${classes.nested}`}
          />
        </List>
      </Collapse>

      {/* DROPDOWN 2: RELATÓRIOS */}
      <ListItem
        button
        onClick={() => setOpenReportsMenu(!openReportsMenu)}
        className={classes.menuItem}
      >
        <ListItemIcon>
          <BarChartIcon />
        </ListItemIcon>
        <ListItemText primary={i18n.t("mainDrawer.listItems.report")} />
        {openReportsMenu ? <ExpandLess /> : <ExpandMore />}
      </ListItem>

      <Collapse in={openReportsMenu} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemLink
            to="/completed-by-analyst"
            primary={i18n.t("mainDrawer.listItems.completedByAnalyst")}
            icon={<AssignmentIcon />}
            className={`${classes.menuItem} ${classes.nested}`}
          />
          <ListItemLink
            to="/currently-served"
            primary={i18n.t("mainDrawer.listItems.CurrentlyBeingServed")}
            icon={<AssignmentIcon />}
            className={`${classes.menuItem} ${classes.nested}`}
          />
          <ListItemLink
            to="/tarefas-customizadas"
            primary="Tarefas por Período"
            icon={<AssignmentIcon />}
            className={`${classes.menuItem} ${classes.nested}`}
          />
        </List>
      </Collapse>

      {/* DROPDOWN 3: ADMINISTRAÇÃO (Permissão de Admin) */}
      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            <ListItem
              button
              onClick={() => setOpenAdminMenu(!openAdminMenu)}
              className={classes.menuItem}
            >
              <ListItemIcon>
                <SupervisorAccountIcon />
              </ListItemIcon>
              <ListItemText
                primary={i18n.t("mainDrawer.listItems.administration")}
              />
              {openAdminMenu ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            <Collapse in={openAdminMenu} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemLink
                  to="/users"
                  primary={i18n.t("mainDrawer.listItems.users")}
                  icon={<PeopleAltOutlinedIcon />}
                  className={`${classes.menuItem} ${classes.nested}`}
                />
                <ListItemLink
                  to="/queues"
                  primary={i18n.t("mainDrawer.listItems.queues")}
                  icon={<AccountTreeOutlinedIcon />}
                  className={`${classes.menuItem} ${classes.nested}`}
                />
                <ListItemLink
                  to="/settings"
                  primary={i18n.t("mainDrawer.listItems.settings")}
                  icon={<SettingsOutlinedIcon />}
                  className={`${classes.menuItem} ${classes.nested}`}
                />
                <ListItemLink
                  to="/tickets-received"
                  primary={i18n.t("mainDrawer.listItems.TicketsReceived")}
                  icon={<AssignmentIcon />}
                  className={`${classes.menuItem} ${classes.nested}`}
                />
                <ListItemLink
                  to="/backups-status"
                  primary="Status dos Backups"
                  icon={<StorageIcon />}
                  className={`${classes.menuItem} ${classes.nested}`}
                />
              </List>
            </Collapse>
          </>
        )}
      />
    </List>
  );
};

export default MainListItems;